import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { get } from '@/api/client';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { BoardTabs } from '@/components/organisms/BoardTabs';
import { timeAgo, comma } from '@/lib/format';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';

interface DiaryItem {
  id: number;
  ticker: string;
  asset_type: string;
  ticker_name: string | null;
  buy_price: string | null;
  sell_price: string | null;
  quantity: string;
  buy_date: string | null;
  sell_date: string | null;
  profit: string | null;
  profit_rate: string | null;
  memo: string | null;
  is_certified: boolean;
  created_at: string;
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
}

interface Response {
  ticker: string;
  tickerName: string | null;
  list: DiaryItem[];
}

export function PublicDiaryTickerPage() {
  const { ticker } = useParams<{ ticker: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['diaries', 'public', ticker],
    queryFn: () =>
      get<Response>(`/diaries/public/${encodeURIComponent(ticker ?? '')}`),
    enabled: !!ticker,
  });

  const isCoin = ticker?.startsWith('KRW-');
  const displayName = (() => {
    if (!ticker) return '';
    if (isCoin) {
      const kr = COIN_NAMES_KR[ticker] ?? data?.tickerName;
      return kr ? `${kr} (${stripKrw(ticker)})` : stripKrw(ticker);
    }
    return data?.tickerName ? `${data.tickerName} (${ticker})` : ticker;
  })();

  // 합계
  const sold = data?.list.filter((d) => d.sell_date && d.profit_rate);
  const avgProfitRate = sold && sold.length > 0
    ? sold.reduce((s, d) => s + Number(d.profit_rate ?? 0), 0) / sold.length
    : 0;
  const totalProfit = sold?.reduce((s, d) => s + Number(d.profit ?? 0), 0) ?? 0;

  return (
    <>
      {/* 매매일지 게시판 탭 활성 — 다른 게시판 상세와 동일 구조 */}
      <BoardTabs activeSlug="diary" />
      <div className="container-base pb-6">
        <div className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
      {/* 게시판 링크 영역 — 상세 페이지에서 목록(게시판) 으로 돌아갈 진입점 */}
      <div className="mb-3">
        <Link
          to="/board/diary"
          className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-text-sub)] transition-colors hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/5 hover:text-[var(--color-primary)]"
        >
          ← 매매일지 게시판
        </Link>
      </div>
      <header className="mb-5">
        <h1 className="text-2xl font-extrabold">{displayName}</h1>
        <p className="mt-1 text-sm text-[var(--color-text-sub)]">
          유저들의 공개 매매일지 모음
        </p>
      </header>

      {/* 요약 */}
      {data && data.list.length > 0 && (
        <section className="mb-5 grid grid-cols-3 gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-center">
          <div>
            <div className="text-xs text-[var(--color-text-sub)]">공개 일지</div>
            <div className="mt-1 text-lg font-bold">{data.list.length}건</div>
          </div>
          <div>
            <div className="text-xs text-[var(--color-text-sub)]">평균 수익률</div>
            <div
              className={`mt-1 text-lg font-bold tabular-nums ${
                avgProfitRate > 0
                  ? 'text-[var(--color-rise)]'
                  : avgProfitRate < 0
                  ? 'text-[var(--color-fall)]'
                  : 'text-[var(--color-text-sub)]'
              }`}
            >
              {avgProfitRate > 0 ? '+' : ''}
              {avgProfitRate.toFixed(2)}%
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--color-text-sub)]">합계 손익</div>
            <div
              className={`mt-1 text-lg font-bold tabular-nums ${
                totalProfit > 0
                  ? 'text-[var(--color-rise)]'
                  : totalProfit < 0
                  ? 'text-[var(--color-fall)]'
                  : 'text-[var(--color-text-sub)]'
              }`}
            >
              {totalProfit >= 0 ? '+' : ''}
              {comma(Math.round(totalProfit))}원
            </div>
          </div>
        </section>
      )}

      {/* 리스트 */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : data && data.list.length > 0 ? (
        <ul className="space-y-2">
          {data.list.map((d) => {
            const isSell = !!d.sell_date;
            const rate = Number(d.profit_rate ?? 0);
            const profit = Number(d.profit ?? 0);
            const date = d.sell_date ?? d.buy_date ?? d.created_at.slice(0, 10);
            const price = isSell ? d.sell_price : d.buy_price;

            return (
              <li
                key={d.id}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
              >
                <div className="flex items-start gap-3">
                  <Link to={`/users/${d.user_id}`} className="shrink-0">
                    <Avatar
                      src={d.profile_image_url ?? undefined}
                      alt={d.nickname}
                      size="sm"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/users/${d.user_id}`}
                        className="text-sm font-semibold hover:underline"
                      >
                        {d.nickname}
                      </Link>
                      <LevelBadge level={d.level} size="sm" />
                      <span className="text-xs text-[var(--color-text-sub)]">
                        · {timeAgo(date)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className={isSell ? 'badge badge-fall' : 'badge badge-rise'}>
                        {isSell ? '매도' : '매수'}
                      </span>
                      <span className="text-[var(--color-text-sub)]">
                        {Number(d.quantity).toLocaleString('ko-KR')}
                        {d.asset_type === 'coin' ? '개' : '주'} ×{' '}
                        {price ? comma(Number(price)) + '원' : '-'}
                      </span>
                    </div>
                    {d.memo && (
                      <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-sub)]">
                        {d.memo}
                      </p>
                    )}
                  </div>
                  {isSell && (
                    <div className="shrink-0 text-right">
                      <div
                        className={`flex items-center justify-end gap-0.5 text-base font-bold tabular-nums ${
                          rate > 0
                            ? 'text-[var(--color-rise)]'
                            : rate < 0
                            ? 'text-[var(--color-fall)]'
                            : 'text-[var(--color-text-sub)]'
                        }`}
                      >
                        {rate > 0 ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : rate < 0 ? (
                          <TrendingDown className="h-4 w-4" />
                        ) : null}
                        {rate > 0 ? '+' : ''}
                        {rate.toFixed(2)}%
                      </div>
                      <div className="text-xs text-[var(--color-text-sub)] tabular-nums">
                        {profit >= 0 ? '+' : ''}
                        {comma(Math.round(profit))}원
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-sm text-[var(--color-text-sub)]">
          공개된 매매일지가 없어요.
        </div>
      )}
        </div>
      </div>
    </>
  );
}
